import { useEffect, useRef, useState } from "react";

import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { LP_CHROME_CSS } from "@/lib/lp-chrome-css";
import { LVT_CSS, LVT_HTML_HARIDWAR } from "@/lib/lvt-haridwar-lp";
import { LVT_HTML_AGRA } from "@/lib/lvt-agra-lp";
import { LVT_HTML_GURGAON } from "@/lib/lvt-gurgaon-lp";
import { separateLandingPageDisclaimer } from "@/lib/landing-page-disclaimer";
import { notifyForm } from "@/lib/notifyForm";

function istHour(): number {
  try {
    return parseInt(
      new Date().toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        hour12: false,
      }),
      10,
    );
  } catch {
    return 12;
  }
}

export function LaserTighteningCostPage({
  city = "Haridwar",
}: { city?: "Haridwar" | "Agra" | "Gurgaon" } = {}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [ctaMode, setCtaMode] = useState<"active" | "off">("active");
  const pageHtml = city === "Agra" ? LVT_HTML_AGRA : city === "Gurgaon" ? LVT_HTML_GURGAON : LVT_HTML_HARIDWAR;
  const { contentHtml, disclaimerHtml } = separateLandingPageDisclaimer(pageHtml);

  useEffect(() => {
    const h = istHour();
    setCtaMode(h >= 9 && h < 20 ? "active" : "off");
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups: Array<() => void> = [];

    const leadModal = root.querySelector<HTMLElement>("#lead-modal");
    const callModal = root.querySelector<HTMLElement>("#call-modal");
    const formContainer = root.querySelector<HTMLElement>("#modal-form-container");
    const thankYou = root.querySelector<HTMLElement>("#thank-you");

    const openModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.add("open");
      modal.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("modal-open");
      const dlg = modal.querySelector<HTMLElement>(".modal-dialog");
      if (dlg) dlg.scrollTop = 0;
    };
    const closeModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.remove("open");
      modal.setAttribute("aria-hidden", "true");
      if (!root.querySelector(".modal-backdrop.open")) {
        document.documentElement.classList.remove("modal-open");
      }
    };
    const closeAll = () => {
      root.querySelectorAll<HTMLElement>(".modal-backdrop.open").forEach(closeModal);
    };
    const openLeadForm = (doctor = "") => {
      const doctorField = root.querySelector<HTMLInputElement>("#m-doctor");
      const pageField = root.querySelector<HTMLInputElement>("#m-pageurl");
      if (doctorField) doctorField.value = doctor;
      if (pageField) pageField.value = window.location.href;
      if (formContainer) formContainer.style.display = "";
      if (thankYou) thankYou.classList.remove("active");
      openModal(leadModal);
      setTimeout(() => {
        root.querySelector<HTMLInputElement>("#m-name")?.focus({ preventScroll: true });
      }, 350);
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const closeBtn = target?.closest("[data-modal-close]");
      if (closeBtn) {
        e.preventDefault();
        closeModal(closeBtn.closest(".modal-backdrop") as HTMLElement);
        return;
      }
      const opener = target?.closest("[data-open-form]");
      if (opener) {
        e.preventDefault();
        openLeadForm(opener.getAttribute("data-doctor") || "");
      }
    };
    root.addEventListener("click", onClick);
    cleanups.push(() => root.removeEventListener("click", onClick));

    root.querySelectorAll<HTMLElement>(".modal-backdrop").forEach((bd) => {
      const handler = (e: Event) => {
        if (e.target === bd) closeModal(bd);
      };
      bd.addEventListener("click", handler);
      cleanups.push(() => bd.removeEventListener("click", handler));
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeAll();
    };
    document.addEventListener("keydown", onKey);
    cleanups.push(() => document.removeEventListener("keydown", onKey));

    const isDesktop = () => window.matchMedia("(min-width: 900px) and (pointer: fine)").matches;
    const isOffHours = () => root.dataset.ctaMode === "off";

    const showToast = (msg: string) => {
      const existing = document.getElementById("off-hours-toast");
      if (existing) existing.remove();
      const t = document.createElement("div");
      t.id = "off-hours-toast";
      t.setAttribute("role", "status");
      t.textContent = msg;
      root.appendChild(t);
      void t.offsetWidth;
      t.classList.add("show");
      setTimeout(() => {
        t.classList.remove("show");
        setTimeout(() => t.remove(), 350);
      }, 4500);
    };

    root.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((link) => {
      const handler = (e: Event) => {
        if (isOffHours()) {
          e.preventDefault();
          if (
            link.classList.contains("top-phone") ||
            link.classList.contains("foot-phone") ||
            link.classList.contains("loc-phone-link")
          ) {
            showToast(
              "Our team is not active right now. Fill the form and we will call you back privately in active hours.",
            );
          }
          openLeadForm();
          return;
        }
        if (isDesktop()) {
          e.preventDefault();
          openModal(callModal);
        }
      };
      link.addEventListener("click", handler);
      cleanups.push(() => link.removeEventListener("click", handler));
    });

    const form = root.querySelector<HTMLFormElement>("#modal-form");
    if (form) {
      const handler = (e: Event) => {
        e.preventDefault();
        const data = new FormData(form);
        const name = String(data.get("name") ?? "").trim();
        const phone = String(data.get("phone") ?? "").trim();
        void notifyForm({
          formType: "Private Laser Vaginal Tightening Consultation",
          source: `${city} Laser Vaginal Tightening Cost Page`,
          fields: [
            { label: "Name", value: name },
            { label: "Phone", value: phone },
            { label: "Treatment", value: "Laser Vaginal Tightening" },
            { label: "City", value: city },
            { label: "Doctor", value: String(data.get("doctor") ?? "") },
            { label: "Page", value: window.location.href },
          ],
        });
        const tyName = root.querySelector("#ty-name");
        if (tyName) tyName.textContent = (name || "friend").split(" ")[0];
        form.reset();
        if (formContainer) formContainer.style.display = "none";
        thankYou?.classList.add("active");
        openModal(leadModal);
        const dlg = leadModal?.querySelector<HTMLElement>(".modal-dialog");
        if (dlg) dlg.scrollTop = 0;
      };
      form.addEventListener("submit", handler);
      cleanups.push(() => form.removeEventListener("submit", handler));
    }

    const mapFrame = root.querySelector<HTMLElement>("#map-frame");
    if (mapFrame && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const src = mapFrame.getAttribute("data-map-src");
            if (src && !mapFrame.querySelector("iframe")) {
              const iframe = document.createElement("iframe");
              iframe.src = src;
              iframe.width = "100%";
              iframe.height = "100%";
              iframe.loading = "lazy";
              iframe.style.border = "0";
              iframe.title = `Healic Multispeciality Hospital ${city} — map`;
              iframe.referrerPolicy = "no-referrer-when-downgrade";
              mapFrame.appendChild(iframe);
            }
            observer.disconnect();
          });
        },
        { rootMargin: "300px" },
      );
      observer.observe(mapFrame);
      cleanups.push(() => observer.disconnect());
    }

    return () => {
      cleanups.forEach((fn) => fn());
      document.documentElement.classList.remove("modal-open");
    };
  }, [city]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: LVT_CSS + LP_CHROME_CSS }} />
      <div className="lp-chrome">
        <SiteHeader />
      </div>
      <div
        ref={rootRef}
        data-cta-mode={ctaMode}
        dangerouslySetInnerHTML={{ __html: contentHtml }}
      />
      <div className="lp-chrome">
        <SiteFooter />
      </div>
      {disclaimerHtml && <div dangerouslySetInnerHTML={{ __html: disclaimerHtml }} />}
    </>
  );
}
